import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Columns3, Table2 } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { TableSortIcon } from 'components/TableSortIcon'
import { cn } from 'lib/utils'

import { ExplorerCell } from './ExplorerCell'
import { EMPTY_GROUP_KEY, groupRows } from '../utils/explorerFormat'
import type { ExplorerColumn, ExplorerRow, ExplorerSort } from '../types/explorer.types'

/** Default width of the pinned identity column, before any user resize. */
const IDENTITY_WIDTH = 220

/**
 * Resize bounds, matching the server (MYD-379).
 *
 * Below the minimum a column is too narrow to carry a header; above the
 * maximum one column pushes every other off the page, which on a grid that
 * scrolls sideways is easy to do by accident and awkward to undo.
 */
const MIN_COLUMN_WIDTH = 80
const MAX_COLUMN_WIDTH = 600

/** Slack added when fitting to content, so text is not flush to the border. */
const FIT_PADDING = 26

/** Width of the selection column (MYD-506), and so the pinned identity column's offset. */
const SELECT_WIDTH = 40

/** Row selection, by row id (MYD-506). */
export interface ExplorerGridSelection {
	selected: ReadonlySet<string>
	onChange: (next: Set<string>) => void
}

/** A checkbox that can also show "some" (indeterminate), which has no HTML attribute. */
function SelectBox({ checked, indeterminate = false, label, onClick }: { checked: boolean; indeterminate?: boolean; label: string; onClick: (event: React.MouseEvent<HTMLInputElement>) => void }) {
	return (
		<input
			type="checkbox"
			aria-label={label}
			checked={checked}
			ref={element => {
				if (element) element.indeterminate = indeterminate
			}}
			// The change is applied in onClick, which carries shiftKey.
			onChange={() => undefined}
			onClick={onClick}
			className="h-4 w-4 cursor-pointer accent-primary align-middle"
		/>
	)
}

interface ExplorerGridProps {
	/** Ordered column definitions for the selected keys. */
	columns: ExplorerColumn[]
	rows: ExplorerRow[]
	sorts: ExplorerSort[]
	groupBy: string | null
	/** Column widths in pixels, keyed by column key. Absent means auto. */
	widths?: Record<string, number>
	/** Commit a resize. `null` clears the width so the column sizes itself. */
	onResizeColumn?: (key: string, width: number | null) => void
	/** Collapsed group keys, lifted so a toolbar can expand or collapse all. */
	collapsedGroups?: Set<string>
	onToggleGroup?: (key: string) => void
	onToggleSort: (key: string) => void
	isLoading?: boolean
	/** True when the empty result is the consequence of a filter. */
	isFiltered?: boolean
	rowLabel?: string
	onPickColumns?: () => void
	onClearFilters?: () => void
	/**
	 * A selection column (MYD-506): a checkbox per row, select-all for the
	 * page, shift-click for a range. Absent, the grid has no selection at all,
	 * which is the default: only grains a presentation can be built from turn
	 * it on.
	 */
	selection?: ExplorerGridSelection
}

/**
 * The Explorer results grid (MYD-360).
 *
 * Built on the ShadCN table primitive, matching the conventions in
 * `TasksCN_Index` and `DocumentLibraryList`. Two things it does that neither of
 * those needs:
 *
 *  - **A sticky header and a pinned first column.** A report is routinely 20
 *    columns wide, and scrolling sideways past the row's own name makes it
 *    impossible to tell which row you are reading.
 *  - **Group headers.** Grouping is applied server-side as a leading sort, so
 *    equal values arrive contiguous and the grid only has to draw the breaks.
 *
 * The grid holds no column knowledge of its own. Everything is driven by the
 * server's catalog definitions, which is what makes a new backend column appear
 * here, correctly formatted and sortable, with no frontend change.
 */
export function ExplorerGrid({
	columns,
	rows,
	sorts,
	groupBy,
	widths,
	onResizeColumn,
	collapsedGroups,
	onToggleGroup,
	onToggleSort,
	isLoading = false,
	isFiltered = false,
	rowLabel = 'row',
	onPickColumns,
	onClearFilters,
	selection,
}: ExplorerGridProps) {
	const tableRef = useRef<HTMLTableElement>(null)
	// The width being dragged RIGHT NOW, held locally so the definition is not
	// rewritten on every pointer move: that would mark the report dirty dozens
	// of times a second and re-render the whole screen through the hook.
	const [dragging, setDragging] = useState<{ key: string; width: number } | null>(null)
	// The row a shift-click extends from: the last one clicked.
	const anchorRow = useRef<number | null>(null)

	const widthFor = useCallback(
		(column: ExplorerColumn, index: number): number | undefined => {
			if (dragging?.key === column.key) return dragging.width
			const stored = widths?.[column.key]
			if (typeof stored === "number") return stored
			// Only the identity column has an intrinsic default: it is pinned, so
			// its width sets the sticky offset for everything to its right.
			return index === 0 ? IDENTITY_WIDTH : undefined
		},
		[widths, dragging],
	)

	const clamp = (width: number) => Math.round(Math.min(Math.max(width, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH))

	/**
	 * Drag to resize, using pointer capture.
	 *
	 * Capture rather than window listeners because the pointer routinely leaves
	 * the 5px handle mid-drag; without it the resize stops the moment the cursor
	 * strays, which feels broken rather than precise.
	 */
	const startResize = (event: React.PointerEvent<HTMLElement>, column: ExplorerColumn, index: number) => {
		if (!onResizeColumn) return
		// The handle sits inside a header that sorts on click. Without this the
		// drag would also reorder the report.
		event.preventDefault()
		event.stopPropagation()

		// Held in a local, NOT read off the event later. React resets
		// `currentTarget` to null once the handler returns, so a closure that
		// reaches for it during pointerup gets null and throws — which silently
		// skipped the commit and left `dragging` stuck on the last value, so the
		// column looked resized and nothing was ever saved.
		const grip = event.currentTarget as HTMLElement
		const header = (event.target as HTMLElement).closest('th')
		const startX = event.clientX
		const startWidth =
			widthFor(column, index) ?? header?.getBoundingClientRect().width ?? IDENTITY_WIDTH

		// Capture is an optimisation, not a requirement: it keeps the drag alive
		// when the cursor leaves the 5px grip. It throws for a pointer the browser
		// no longer considers active, and losing the whole resize over that would
		// be worse than a drag that stops early.
		try {
			grip.setPointerCapture(event.pointerId)
		} catch {
			// Nothing to do — the listeners below still drive the resize.
		}
		setDragging({ key: column.key, width: clamp(startWidth) })

		const move = (moveEvent: PointerEvent) => {
			setDragging({ key: column.key, width: clamp(startWidth + (moveEvent.clientX - startX)) })
		}

		const finish = (upEvent: PointerEvent) => {
			grip.removeEventListener('pointermove', move)
			grip.removeEventListener('pointerup', finish)
			grip.removeEventListener('pointercancel', finish)
			try {
				grip.releasePointerCapture(upEvent.pointerId)
			} catch {
				// Already released, or never captured.
			}
			setDragging(null)
			onResizeColumn(column.key, clamp(startWidth + (upEvent.clientX - startX)))
		}

		grip.addEventListener('pointermove', move)
		grip.addEventListener('pointerup', finish)
		grip.addEventListener('pointercancel', finish)
	}

	/**
	 * Double-click a divider to fit the column to its content.
	 *
	 * Measures what is actually rendered — the header and this page of cells —
	 * rather than guessing from the value lengths, so a badge or a truncated
	 * link is measured as it draws rather than as its text.
	 */
	const fitToContent = (column: ExplorerColumn, index: number) => {
		if (!onResizeColumn) return
		const table = tableRef.current
		if (!table) return

		const cells = table.querySelectorAll<HTMLElement>(
			`tr > *:nth-child(${index + 1 + (selection ? 1 : 0)})`,
		)
		let widest = 0
		for (const cell of cells) {
			// A group header spans every column and would measure as the full table.
			if (cell.hasAttribute("colspan")) continue
			const content = (cell.firstElementChild as HTMLElement) ?? cell
			widest = Math.max(widest, content.scrollWidth)
		}
		if (widest === 0) return

		onResizeColumn(column.key, clamp(widest + FIT_PADDING))
	}

	const activeSort = sorts[0] ?? null
	const groups = useMemo(() => groupRows(rows, groupBy), [rows, groupBy])
	const groupColumn = useMemo(
		() => columns.find(column => column.key === groupBy) ?? null,
		[columns, groupBy],
	)

	// The circular variant, because `containerHeight` is only honoured there:
	// on the default linear variant it is silently ignored, and a thin bar at
	// the top of an otherwise blank frame does not read as "the grid is loading".
	if (isLoading) return <Spinner type="circular" containerHeight="50vh" />

	if (columns.length === 0) {
		return (
			<EmptyState
				icon={<Columns3 className="w-6 h-6 text-gray-400" />}
				title="No columns selected"
				body="Pick the columns you want to see and the report will build itself."
				action={
					onPickColumns ? (
						<Button onClick={onPickColumns}>
							<Columns3 className="w-4 h-4 mr-2" />
							Choose columns
						</Button>
					) : null
				}
			/>
		)
	}

	if (rows.length === 0) {
		// "Nothing matched your filters" and "there is nothing here" are different
		// problems with different fixes, and conflating them sends a user hunting
		// for data that was only ever filtered out.
		return isFiltered ? (
			<EmptyState
				icon={<Table2 className="w-6 h-6 text-gray-400" />}
				title={`No ${rowLabel}s match your filters`}
				body="Try widening or removing a filter."
				action={
					onClearFilters ? (
						// This Button is not the stock ShadCN one: it has no "outline"
						// variant, and the outlined look comes from the isOutline prop.
						<Button variant="ghost" isOutline onClick={onClearFilters}>
							Clear filters
						</Button>
					) : null
				}
			/>
		) : (
			<EmptyState
				icon={<Table2 className="w-6 h-6 text-gray-400" />}
				title={`No ${rowLabel}s yet`}
				body={`Once you have ${rowLabel}s, they will appear here.`}
			/>
		)
	}

	/**
	 * The style that pins a column to a width.
	 *
	 * min AND max, not just width: the table lays out with `auto`, where a
	 * bare `width` is a suggestion the browser overrides as soon as a cell
	 * needs more room — which is exactly what a user resizing a column is
	 * trying to stop.
	 */
	const sizeOf = (column: ExplorerColumn, index: number) => {
		const width = widthFor(column, index)
		return width === undefined ? undefined : { width, minWidth: width, maxWidth: width }
	}

	// The pinned identity column sits just right of the selection column.
	const pinnedStyle = (column: ExplorerColumn, index: number) => ({
		...sizeOf(column, index),
		...(index === 0 && selection ? { left: SELECT_WIDTH } : {}),
	})
	const selectStyle = { width: SELECT_WIDTH, minWidth: SELECT_WIDTH, maxWidth: SELECT_WIDTH }

	const pageIds = rows.map(row => row.id)
	const selectedOnPage = selection ? pageIds.filter(id => selection.selected.has(id)).length : 0

	const toggleAllOnPage = () => {
		if (!selection) return
		const next = new Set(selection.selected)
		if (selectedOnPage === pageIds.length) pageIds.forEach(id => next.delete(id))
		else pageIds.forEach(id => next.add(id))
		selection.onChange(next)
	}

	/**
	 * Toggle one row, or with shift every row from the last one clicked to
	 * this one, in the order they are listed. The range takes the new state of
	 * the row clicked, as in a file manager.
	 */
	const toggleRow = (rowIndex: number, shift: boolean) => {
		if (!selection) return
		const id = pageIds[rowIndex]
		const turnOn = !selection.selected.has(id)
		const next = new Set(selection.selected)
		const from = shift && anchorRow.current !== null ? Math.min(anchorRow.current, rowIndex) : rowIndex
		const to = shift && anchorRow.current !== null ? Math.max(anchorRow.current, rowIndex) : rowIndex
		for (const rangeId of pageIds.slice(from, to + 1)) {
			if (turnOn) next.add(rangeId)
			else next.delete(rangeId)
		}
		anchorRow.current = rowIndex
		selection.onChange(next)
	}

	const renderRow = (row: ExplorerRow) => (
		<TableRow key={row.id} className={cn('hover:bg-gray-50', selection?.selected.has(row.id) && 'bg-primary/5')}>
			{selection && (
				<TableCell className="sticky left-0 z-10 bg-white px-0 text-center" style={selectStyle}>
					<SelectBox
						checked={selection.selected.has(row.id)}
						label={`Select ${String(row[columns[0]?.key] ?? 'row')}`}
						onClick={event => toggleRow(pageIds.indexOf(row.id), event.shiftKey)}
					/>
				</TableCell>
			)}
			{columns.map((column, index) => (
				<TableCell
					key={column.key}
					className={cn(
						'whitespace-nowrap',
						index === 0 &&
							'sticky left-0 z-10 bg-white font-medium text-gray-900 border-r border-gray-200',
					)}
					style={pinnedStyle(column, index)}
				>
					<ExplorerCell
						value={row[column.key]}
						column={column}
						className={index === 0 ? 'block truncate' : undefined}
					/>
				</TableCell>
			))}
		</TableRow>
	)

	return (
		<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white">
			{/* The scroll container is here rather than on the page, so a wide
			    report scrolls inside its own frame and the page never scrolls
			    sideways underneath the toolbar. */}
			<div className="overflow-auto max-h-[calc(100vh-16rem)]">
				<Table ref={tableRef}>
					<TableHeader>
						<TableRow className="bg-gray-50 hover:bg-gray-50">
							{selection && (
								<TableHead className="sticky left-0 top-0 z-30 bg-gray-50 px-0 text-center" style={selectStyle}>
									<SelectBox
										checked={pageIds.length > 0 && selectedOnPage === pageIds.length}
										indeterminate={selectedOnPage > 0 && selectedOnPage < pageIds.length}
										label="Select all on this page"
										onClick={toggleAllOnPage}
									/>
								</TableHead>
							)}
							{columns.map((column, index) => (
								<TableHead
									key={column.key}
									className={cn(
										'sticky top-0 z-20 bg-gray-50 text-xs font-semibold text-gray-700 whitespace-nowrap',
										column.sortable && 'cursor-pointer select-none',
										// The identity column is sticky in BOTH axes, so it
										// needs a higher stacking order than either alone.
										index === 0 && 'left-0 z-30 border-r border-gray-200',
									)}
									style={pinnedStyle(column, index)}
									onClick={column.sortable ? () => onToggleSort(column.key) : undefined}
									aria-sort={
										activeSort?.key === column.key
											? activeSort.direction === 'asc'
												? 'ascending'
												: 'descending'
											: undefined
									}
								>
									<span className="inline-flex items-center">
										{column.label}
										{column.sortable && (
											<TableSortIcon
												columnId={column.key}
												orderBy={activeSort?.key ?? null}
												order={activeSort?.direction ?? 'asc'}
											/>
										)}
									</span>

								{/* The resize grip. A separator rather than a button: it adjusts a
									value by dragging, and announcing it as a button would promise a
									click that does nothing. */}
								{onResizeColumn && (
									<span
										role="separator"
										aria-orientation="vertical"
										aria-label={`Resize ${column.label}`}
										title="Drag to resize, double-click to fit"
										onPointerDown={event => startResize(event, column, index)}
										onDoubleClick={event => {
											event.stopPropagation()
											fitToContent(column, index)
										}}
										className={cn(
											'absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none',
											'hover:bg-primary/40',
											dragging?.key === column.key && 'bg-primary/60',
										)}
									/>
								)}
								</TableHead>
							))}
						</TableRow>
					</TableHeader>

					<TableBody>
						{groups
							// Keyed by POSITION, not by value. Contiguous grouping can emit
							// the same value twice — most visibly in the render between
							// picking a new group column and the re-sorted rows arriving,
							// where the old rows are still in the old order — and
							// `key={group.key}` then collides. `collapsedGroups` stays keyed
							// by value, because the value is what the user is toggling.
							? groups.map((group, index) => {
									const collapsed = collapsedGroups?.has(group.key) ?? false
									return (
										<Fragment key={`${group.key}#${index}`}>
											<TableRow className="bg-gray-50/70 hover:bg-gray-50">
												<TableCell
													colSpan={columns.length + (selection ? 1 : 0)}
													className="sticky left-0 p-0 text-xs font-semibold text-gray-700"
												>
													{/* A real button, not a clickable cell: collapsing a
													    group is an action, and a td with an onClick is
													    unreachable by keyboard and announces nothing. */}
													<button
														type="button"
														onClick={() => onToggleGroup?.(group.key)}
														aria-expanded={!collapsed}
														className="w-full flex items-center gap-1.5 px-4 py-2 text-left hover:bg-gray-100/60"
													>
														{collapsed ? (
															<ChevronRight className="w-3.5 h-3.5 text-gray-400" />
														) : (
															<ChevronDown className="w-3.5 h-3.5 text-gray-400" />
														)}
														{group.key === EMPTY_GROUP_KEY || !groupColumn ? (
															<span className="text-gray-400">
																No {groupColumn?.label ?? 'value'}
															</span>
														) : (
															<ExplorerCell value={group.value} column={groupColumn} />
														)}
														<span className="font-normal text-gray-500">
															({group.rows.length})
														</span>
													</button>
												</TableCell>
											</TableRow>
											{!collapsed && group.rows.map(renderRow)}
										</Fragment>
									)
								})
							: rows.map(renderRow)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}

function EmptyState({
	icon,
	title,
	body,
	action,
}: {
	icon: React.ReactNode
	title: string
	body: string
	action?: React.ReactNode
}) {
	return (
		<div className="flex flex-col items-center justify-center py-12 text-center">
			<div className="rounded-full bg-gray-100 p-3 mb-4">{icon}</div>
			<h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
			<p className="text-sm text-gray-500 mb-4 max-w-sm">{body}</p>
			{action}
		</div>
	)
}

export default ExplorerGrid
